import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import { Button, Input, Select, Card } from "../../components/ui";
import PROVINCE from "../../utils/province";
import ScadenzeCollegateModal, {
  caricaScadenzeCollegate,
  riassuntoScadenze,
} from "../../components/ScadenzeCollegateModal";

const STATI_FORNITORE = [
  { value: "100000000", label: "Attivo" },
  { value: "100000001", label: "Inattivo" },
  { value: "100000002", label: "Sospeso" },
];

const MACROCATEGORIE_FALLBACK = [
  { value: "100000000", label: "Manutenzione" },
  { value: "100000001", label: "Servizi" },
  { value: "100000002", label: "Forniture" },
  { value: "100000003", label: "Consulenza" },
];

function FornitoreOnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  // Modalità modifica se presente ID
  const editId = searchParams.get("id");
  const isEditMode = !!editId;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [errors, setErrors] = useState({});
  const [tipologieIntervento, setTipologieIntervento] = useState([]);
  const [macrocategorie, setMacrocategorie] = useState([]);
  const [selectedTipologie, setSelectedTipologie] = useState([]);
  // Tipologie associate al caricamento: servono a capire quali si stanno scollegando
  const [tipologieIniziali, setTipologieIniziali] = useState([]);
  // Scadenze aperte delle tipologie che si stanno scollegando: { scadenze, puoEliminare }
  const [scadenzeCollegate, setScadenzeCollegate] = useState(null);
  const [rsaList, setRsaList] = useState([]);
  const [selectedRsa, setSelectedRsa] = useState([]);
  const [tipologieSearch, setTipologieSearch] = useState("");

  // Form state - tutti i campi dalla tabella phc_fornitore
  const [formData, setFormData] = useState({
    // Dati identificativi
    phc_name: "",
    phc_codiceinterno: "",
    phc_partitaiva: "",
    phc_codicefiscale: "",

    // Sede legale
    phc_indirizzosedelegale: "",
    phc_comunesedelegale: "",
    phc_provinciasedelegale: "",
    phc_capsedelegale: "",
    phc_nazione: "Italia",

    // Sede operativa
    phc_indirizzooperativo: "",

    // Contatti
    phc_telefono: "",
    phc_emailprincipale: "",
    phc_emailoperativa: "",
    phc_pec: "",
    phc_sitoweb: "",

    // Dati bancari
    phc_iban: "",

    // Referente
    phc_nomereferentefornitore: "",
    phc_telefonoreferentefornitore: "",
    phc_emailreferentefornitore: "",

    // Configurazione
    phc_macrocategoria: "",
    phc_stato: "100000000", // Default: Attivo

    // Accesso portale
    phc_abilitatoportale: false,
    phc_emailaccessoportale: "",

    // Note
    phc_note: "",
  });

  // Carica tipologie intervento e lista RSA
  useEffect(() => {
    const loadData = async () => {
      const [tipologieRes, rsaRes, macrocategorieRes] = await Promise.all([
        apiRequest("/tipologie-intervento").catch(() => ({ value: [] })),
        apiRequest("/rsa").catch(() => ({ value: [] })),
        apiRequest("/tipologie-intervento/macrocategorie").catch(() => null),
      ]);
      setTipologieIntervento(tipologieRes.value || []);
      setMacrocategorie(macrocategorieRes || MACROCATEGORIE_FALLBACK);
      setRsaList(rsaRes.value || []);
    };
    loadData();
  }, [apiRequest]);

  // Carica dati fornitore esistente in modalità modifica
  useEffect(() => {
    if (!isEditMode) return;

    const loadFornitore = async () => {
      setLoadingData(true);
      try {
        // Carica dati fornitore, tipologie associate e RSA associate in parallelo
        const [data, tipologieRes, rsaRes] = await Promise.all([
          apiRequest(`/fornitori/${editId}`),
          apiRequest(`/fornitori/${editId}/tipologie-intervento`).catch(() => ({
            value: [],
          })),
          apiRequest(`/fornitori/${editId}/rsa`).catch(() => ({ value: [] })),
        ]);

        // Popola il form con i dati esistenti
        setFormData({
          phc_name: data.nome || data.phc_name || "",
          phc_codiceinterno: data.codiceInterno || data.phc_codiceinterno || "",
          phc_partitaiva: data.partitaIva || data.phc_partitaiva || "",
          phc_codicefiscale: data.codiceFiscale || data.phc_codicefiscale || "",
          phc_indirizzosedelegale:
            data.indirizzoSedeLegale || data.phc_indirizzosedelegale || "",
          phc_comunesedelegale:
            data.comuneSedeLegale || data.phc_comunesedelegale || "",
          phc_provinciasedelegale:
            data.provinciaSedeLegale || data.phc_provinciasedelegale || "",
          phc_capsedelegale: data.capSedeLegale || data.phc_capsedelegale || "",
          phc_nazione: data.nazione || data.phc_nazione || "Italia",
          phc_indirizzooperativo:
            data.indirizzoOperativo || data.phc_indirizzooperativo || "",
          phc_telefono: data.telefono || data.phc_telefono || "",
          phc_emailprincipale:
            data.emailPrincipale || data.phc_emailprincipale || "",
          phc_emailoperativa:
            data.emailOperativa || data.phc_emailoperativa || "",
          phc_pec: data.pec || data.phc_pec || "",
          phc_sitoweb: data.sitoWeb || data.phc_sitoweb || "",
          phc_iban: data.iban || data.phc_iban || "",
          phc_nomereferentefornitore:
            data.nomeReferente || data.phc_nomereferentefornitore || "",
          phc_telefonoreferentefornitore:
            data.telefonoReferente || data.phc_telefonoreferentefornitore || "",
          phc_emailreferentefornitore:
            data.emailReferente || data.phc_emailreferentefornitore || "",
          phc_macrocategoria:
            data.macrocategoria != null
              ? String(data.macrocategoria)
              : data.phc_macrocategoria != null
                ? String(data.phc_macrocategoria)
                : "",
          phc_stato:
            data.stato !== undefined
              ? String(data.stato)
              : data.phc_stato !== undefined
                ? String(data.phc_stato)
                : "100000000",
          phc_abilitatoportale:
            data.abilitatoPortale || data.phc_abilitatoportale || false,
          phc_emailaccessoportale:
            data.emailAccessoPortale || data.phc_emailaccessoportale || "",
          phc_note: data.note || data.phc_note || "",
        });

        // Carica tipologie associate dall'endpoint dedicato
        if (tipologieRes.value && Array.isArray(tipologieRes.value)) {
          setSelectedTipologie(tipologieRes.value.map((t) => t.id));
          setTipologieIniziali(tipologieRes.value.map((t) => t.id));
        }

        // Carica RSA associate dall'endpoint dedicato
        if (rsaRes.value && Array.isArray(rsaRes.value)) {
          setSelectedRsa(rsaRes.value.map((r) => r.id));
        }
      } catch (error) {
        console.error("Error loading fornitore:", error);
        showToast("Errore nel caricamento del fornitore", "error");
        navigate("/fornitori");
      } finally {
        setLoadingData(false);
      }
    };

    loadFornitore();
  }, [editId, isEditMode, apiRequest, navigate, showToast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleTipologiaToggle = (tipologiaId) => {
    setSelectedTipologie((prev) => {
      if (prev.includes(tipologiaId)) {
        return prev.filter((id) => id !== tipologiaId);
      }
      return [...prev, tipologiaId];
    });
  };

  const handleRsaToggle = (rsaId) => {
    setSelectedRsa((prev) => {
      if (prev.includes(rsaId)) {
        return prev.filter((id) => id !== rsaId);
      }
      return [...prev, rsaId];
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.phc_name.trim()) {
      newErrors.phc_name = "La ragione sociale è obbligatoria";
    }

    // Partita IVA validation (11 cifre)
    if (
      formData.phc_partitaiva &&
      !/^\d{11}$/.test(formData.phc_partitaiva.trim())
    ) {
      newErrors.phc_partitaiva = "Partita IVA non valida (11 cifre)";
    }

    // Codice Fiscale validation (11 o 16 caratteri)
    if (
      formData.phc_codicefiscale &&
      !/^[A-Z0-9]{11}$|^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i.test(
        formData.phc_codicefiscale.trim(),
      )
    ) {
      newErrors.phc_codicefiscale = "Codice Fiscale non valido";
    }

    // Email validation
    const emailFields = [
      "phc_emailprincipale",
      "phc_emailoperativa",
      "phc_pec",
      "phc_emailreferentefornitore",
      "phc_emailaccessoportale",
    ];
    emailFields.forEach((field) => {
      if (
        formData[field] &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[field])
      ) {
        newErrors[field] = "Email non valida";
      }
    });

    // CAP validation
    if (
      formData.phc_capsedelegale &&
      !/^\d{5}$/.test(formData.phc_capsedelegale)
    ) {
      newErrors.phc_capsedelegale = "CAP non valido (5 cifre)";
    }

    // IBAN validation (basic)
    if (
      formData.phc_iban &&
      !/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/i.test(
        formData.phc_iban.replace(/\s/g, ""),
      )
    ) {
      newErrors.phc_iban = "IBAN non valido";
    }

    // Se abilitato portale, email accesso obbligatoria
    if (formData.phc_abilitatoportale && !formData.phc_emailaccessoportale) {
      newErrors.phc_emailaccessoportale =
        "Email obbligatoria per accesso al portale";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast("Correggi gli errori nel form", "error");
      return;
    }

    salvaFornitore();
  };

  // Senza sceltaScadenze, se le tipologie tolte hanno scadenze aperte del fornitore,
  // si chiede prima cosa farne.
  const salvaFornitore = async (sceltaScadenze = null) => {
    setLoading(true);

    try {
      if (isEditMode && !sceltaScadenze) {
        const collegate = await caricaScadenzeCollegate(
          apiRequest,
          editId,
          tipologieIniziali,
          selectedTipologie,
        );
        if (collegate) {
          setScadenzeCollegate(collegate);
          return;
        }
      }
      setScadenzeCollegate(null);

      // Prepara i dati per Dataverse (senza le relazioni N:N che vanno salvate a parte)
      const dataverseData = {
        ...formData,
        phc_macrocategoria: formData.phc_macrocategoria
          ? parseInt(formData.phc_macrocategoria)
          : null,
        phc_stato: parseInt(formData.phc_stato),
      };

      // Rimuovi campi che non appartengono all'entità fornitore
      delete dataverseData.tipologieInterventoIds;

      // Rimuovi campi vuoti
      Object.keys(dataverseData).forEach((key) => {
        if (dataverseData[key] === "" || dataverseData[key] === null) {
          delete dataverseData[key];
        }
      });

      let fornitoreId = editId;

      if (isEditMode) {
        await apiRequest(`/fornitori/${editId}`, {
          method: "PUT",
          body: JSON.stringify(dataverseData),
        });
      } else {
        const result = await apiRequest("/fornitori", {
          method: "POST",
          body: JSON.stringify(dataverseData),
        });
        fornitoreId = result.id;
      }

      // Salva le associazioni tipologie intervento (endpoint dedicato)
      const warnings = [];
      try {
        const tipResult = await apiRequest(
          `/fornitori/${fornitoreId}/tipologie-intervento`,
          {
            method: "PUT",
            body: JSON.stringify({
              tipologieIds: selectedTipologie,
              scadenzeCollegate: sceltaScadenze || "lascia",
            }),
          },
        );
        if (tipResult.warning || tipResult.errors?.length > 0) {
          warnings.push(
            "Alcune tipologie intervento non sono state salvate correttamente",
          );
        }
        if (
          tipResult.scadenzeError ||
          tipResult.scadenze?.nonEliminate.length > 0
        ) {
          warnings.push(`Scadenze collegate: ${riassuntoScadenze(tipResult)}`);
        }
      } catch (tipErr) {
        console.warn("Could not save tipologie associations:", tipErr);
        warnings.push("Errore nel salvataggio delle tipologie intervento");
      }

      // Salva le associazioni RSA (endpoint dedicato)
      try {
        await apiRequest(`/fornitori/${fornitoreId}/rsa`, {
          method: "PUT",
          body: JSON.stringify({ rsaIds: selectedRsa }),
        });
      } catch (rsaErr) {
        console.warn("Could not save RSA associations:", rsaErr);
        warnings.push("Errore nel salvataggio delle RSA associate");
      }

      if (warnings.length > 0) {
        showToast(
          `Fornitore salvato ma con avvisi: ${warnings.join(". ")}`,
          "warning",
        );
      } else {
        showToast(
          isEditMode
            ? "Fornitore aggiornato con successo!"
            : "Fornitore creato con successo!",
          "success",
        );
      }
      navigate(`/fornitori/${fornitoreId}`);
    } catch (error) {
      console.error("Error saving fornitore:", error);
      showToast(
        error.message || "Errore durante il salvataggio del fornitore",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Filtra tipologie per ricerca
  const filteredTipologie = tipologieIntervento.filter((tip) => {
    if (!tipologieSearch.trim()) return true;
    const search = tipologieSearch.toLowerCase();
    return (
      (tip.nome || "").toLowerCase().includes(search) ||
      (tip.descrizione || "").toLowerCase().includes(search)
    );
  });

  if (loadingData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <a href="/admin" className="hover:text-primary-600">
            Admin
          </a>
          <span>/</span>
          <span>{isEditMode ? "Modifica Fornitore" : "Nuovo Fornitore"}</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? "Modifica Fornitore" : "Registrazione Nuovo Fornitore"}
        </h1>
        <p className="text-gray-500 mt-1">
          {isEditMode
            ? "Modifica i dati del fornitore"
            : "Inserisci i dati del nuovo fornitore di servizi manutentivi"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sezione: Dati Identificativi */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🏢</span> Dati Identificativi
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Ragione Sociale *"
                name="phc_name"
                value={formData.phc_name}
                onChange={handleChange}
                placeholder="es. Manutenzioni Srl"
                error={errors.phc_name}
                maxLength={200}
              />
            </div>

            <Input
              label="Codice Interno"
              name="phc_codiceinterno"
              value={formData.phc_codiceinterno}
              onChange={handleChange}
              placeholder="es. FOR001"
              helperText="Codice interno identificativo"
              maxLength={20}
            />

            <Select
              label="Macrocategoria"
              name="phc_macrocategoria"
              value={formData.phc_macrocategoria}
              onChange={handleChange}
              options={macrocategorie}
              placeholder="Seleziona categoria principale..."
            />

            <Input
              label="Partita IVA"
              name="phc_partitaiva"
              value={formData.phc_partitaiva}
              onChange={handleChange}
              placeholder="12345678901"
              error={errors.phc_partitaiva}
              maxLength={11}
            />

            <Input
              label="Codice Fiscale"
              name="phc_codicefiscale"
              value={formData.phc_codicefiscale}
              onChange={handleChange}
              placeholder="12345678901 o RSSMRA80A01H501X"
              error={errors.phc_codicefiscale}
              maxLength={16}
            />

            <Select
              label="Stato"
              name="phc_stato"
              value={formData.phc_stato}
              onChange={handleChange}
              options={STATI_FORNITORE}
            />
          </div>
        </Card>

        {/* Sezione: Sede Legale */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📍</span> Sede Legale
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Indirizzo"
                name="phc_indirizzosedelegale"
                value={formData.phc_indirizzosedelegale}
                onChange={handleChange}
                placeholder="Via/Piazza, numero civico"
                maxLength={200}
              />
            </div>

            <Input
              label="Comune"
              name="phc_comunesedelegale"
              value={formData.phc_comunesedelegale}
              onChange={handleChange}
              placeholder="es. Padova"
              maxLength={100}
            />

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Provincia"
                name="phc_provinciasedelegale"
                value={formData.phc_provinciasedelegale}
                onChange={handleChange}
                options={PROVINCE}
                placeholder="Seleziona..."
              />

              <Input
                label="CAP"
                name="phc_capsedelegale"
                value={formData.phc_capsedelegale}
                onChange={handleChange}
                placeholder="35100"
                error={errors.phc_capsedelegale}
                maxLength={5}
              />
            </div>

            <Input
              label="Nazione"
              name="phc_nazione"
              value={formData.phc_nazione}
              onChange={handleChange}
              placeholder="Italia"
              maxLength={50}
            />

            <div className="md:col-span-2">
              <Input
                label="Indirizzo Operativo"
                name="phc_indirizzooperativo"
                value={formData.phc_indirizzooperativo}
                onChange={handleChange}
                placeholder="Indirizzo sede operativa (se diverso da sede legale)"
                maxLength={200}
              />
            </div>
          </div>
        </Card>

        {/* Sezione: Contatti */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📞</span> Contatti
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Telefono"
              name="phc_telefono"
              value={formData.phc_telefono}
              onChange={handleChange}
              placeholder="049 1234567"
              maxLength={20}
            />

            <Input
              label="Email Principale"
              name="phc_emailprincipale"
              type="email"
              value={formData.phc_emailprincipale}
              onChange={handleChange}
              placeholder="info@fornitore.it"
              error={errors.phc_emailprincipale}
              maxLength={100}
            />

            <Input
              label="Email Operativa"
              name="phc_emailoperativa"
              type="email"
              value={formData.phc_emailoperativa}
              onChange={handleChange}
              placeholder="assistenza@fornitore.it"
              error={errors.phc_emailoperativa}
              helperText="Per comunicazioni operative"
              maxLength={100}
            />

            <Input
              label="PEC"
              name="phc_pec"
              type="email"
              value={formData.phc_pec}
              onChange={handleChange}
              placeholder="fornitore@pec.it"
              error={errors.phc_pec}
              maxLength={100}
            />

            <Input
              label="Sito Web"
              name="phc_sitoweb"
              value={formData.phc_sitoweb}
              onChange={handleChange}
              placeholder="https://www.fornitore.it"
              maxLength={200}
            />

            <Input
              label="IBAN"
              name="phc_iban"
              value={formData.phc_iban}
              onChange={handleChange}
              placeholder="IT60X0542811101000000123456"
              error={errors.phc_iban}
              maxLength={34}
            />
          </div>
        </Card>

        {/* Sezione: Referente */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>👤</span> Referente Fornitore
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Nome Referente"
              name="phc_nomereferentefornitore"
              value={formData.phc_nomereferentefornitore}
              onChange={handleChange}
              placeholder="Mario Rossi"
              maxLength={100}
            />

            <Input
              label="Telefono Referente"
              name="phc_telefonoreferentefornitore"
              value={formData.phc_telefonoreferentefornitore}
              onChange={handleChange}
              placeholder="333 1234567"
              maxLength={20}
            />

            <Input
              label="Email Referente"
              name="phc_emailreferentefornitore"
              type="email"
              value={formData.phc_emailreferentefornitore}
              onChange={handleChange}
              placeholder="m.rossi@fornitore.it"
              error={errors.phc_emailreferentefornitore}
              maxLength={100}
            />
          </div>
        </Card>

        {/* Sezione: Accesso Portale */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🔐</span> Accesso Portale Web
          </h2>

          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="phc_abilitatoportale"
                checked={formData.phc_abilitatoportale}
                onChange={handleChange}
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-gray-700">
                Abilita accesso al portale web
              </span>
            </label>

            {formData.phc_abilitatoportale && (
              <div className="pl-8">
                <Input
                  label="Email per Accesso Portale *"
                  name="phc_emailaccessoportale"
                  type="email"
                  value={formData.phc_emailaccessoportale}
                  onChange={handleChange}
                  placeholder="accesso@fornitore.it"
                  error={errors.phc_emailaccessoportale}
                  helperText="L'utente riceverà un invito a questa email"
                  maxLength={100}
                />
              </div>
            )}
          </div>
        </Card>

        {/* Sezione: Tipologie Intervento */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span>🔧</span> Tipologie Intervento Abilitate
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Seleziona le tipologie di intervento che questo fornitore può
                eseguire
              </p>
            </div>
            {selectedTipologie.length > 0 && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700">
                {selectedTipologie.length} selezionate
              </span>
            )}
          </div>

          {tipologieIntervento.length > 0 ? (
            <>
              {/* Tipologie selezionate */}
              {selectedTipologie.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Selezionate
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedTipologie.map((id) => {
                      const tip = tipologieIntervento.find((t) => t.id === id);
                      if (!tip) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-primary-50 text-primary-700 border border-primary-200"
                        >
                          {tip.nome}
                          <button
                            type="button"
                            onClick={() => handleTipologiaToggle(id)}
                            className="text-primary-400 hover:text-primary-600"
                          >
                            <svg
                              className="h-3.5 w-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Ricerca */}
              <div className="relative mb-4">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={tipologieSearch}
                  onChange={(e) => setTipologieSearch(e.target.value)}
                  placeholder="Cerca tipologia..."
                  className="form-input w-full pl-10 pr-10"
                />
                {tipologieSearch && (
                  <button
                    type="button"
                    onClick={() => setTipologieSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Lista completa */}
              <div className="border rounded-lg max-h-80 overflow-y-auto">
                {filteredTipologie.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {filteredTipologie.map((tip) => (
                      <label
                        key={tip.id}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedTipologie.includes(tip.id)
                            ? "bg-primary-50"
                            : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTipologie.includes(tip.id)}
                          onChange={() => handleTipologiaToggle(tip.id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 flex-shrink-0"
                        />
                        <span className="text-sm text-gray-700">
                          {tip.nome}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-400 text-sm">
                    Nessuna tipologia trovata per "{tipologieSearch}"
                  </div>
                )}
              </div>

              {tipologieSearch && (
                <div className="mt-2 text-xs text-gray-400">
                  {filteredTipologie.length} di {tipologieIntervento.length}{" "}
                  tipologie
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Caricamento tipologie intervento...</p>
            </div>
          )}
        </Card>

        {/* Sezione: RSA Associate */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🏥</span> RSA Associate
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Seleziona le RSA per cui questo fornitore opera
          </p>

          {rsaList.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {rsaList.map((rsa) => (
                <label
                  key={rsa.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedRsa.includes(rsa.id)
                      ? "bg-primary-50 border-primary-300"
                      : "border-gray-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedRsa.includes(rsa.id)}
                    onChange={() => handleRsaToggle(rsa.id)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {rsa.nome}
                    </div>
                    {rsa.comune && (
                      <div className="text-xs text-gray-500 truncate">
                        {rsa.comune}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Caricamento RSA...</p>
            </div>
          )}

          {selectedRsa.length > 0 && (
            <div className="mt-4 p-3 bg-primary-50 rounded-lg">
              <span className="text-sm text-primary-700">
                {selectedRsa.length} RSA selezionate
              </span>
            </div>
          )}
        </Card>

        {/* Sezione: Note */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📝</span> Note
          </h2>

          <textarea
            name="phc_note"
            value={formData.phc_note}
            onChange={handleChange}
            placeholder="Note aggiuntive sul fornitore..."
            className="form-input w-full h-32"
            maxLength={10000}
          />
        </Card>

        {/* Form Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/admin")}
          >
            ← Annulla
          </Button>

          <div className="flex gap-3">
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {isEditMode ? "Salvataggio..." : "Creazione in corso..."}
                </span>
              ) : isEditMode ? (
                "Salva Modifiche"
              ) : (
                "Crea Fornitore"
              )}
            </Button>
          </div>
        </div>
      </form>

      <ScadenzeCollegateModal
        isOpen={!!scadenzeCollegate}
        scadenze={scadenzeCollegate?.scadenze}
        puoEliminare={scadenzeCollegate?.puoEliminare}
        saving={loading}
        onScelta={salvaFornitore}
        onClose={() => setScadenzeCollegate(null)}
      />
    </div>
  );
}

export default FornitoreOnboardingPage;
